import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { idOf, type Entity } from '../api.js';
import { hasCorrections, heldByMachine } from '../described.js';
import { t } from '../i18n.js';
import {
  failReasonLabel,
  formatCoordinates,
  formatDate,
  formatDateTime,
  formatSize,
} from '../format.js';
import {
  albums,
  describing,
  detail,
  detailBusy,
  emojiPalette,
  folders,
  groups,
  me,
  members,
  reactors,
  selection,
  shares,
  view,
  ROOT,
} from '../state.js';
import {
  closeInspector,
  loadMembers,
  onDeleteEntity,
  onDownload,
  onLoadReactors,
  onLoadShares,
  onModerateReaction,
  onMove,
  onPickCollection,
  onPickPoster,
  onReact,
  onRemoveFromAlbum,
  onRegenerate,
  onRename,
  onRetryDerivatives,
  onSaveDescribed,
  onRevokeGroup,
  onUnblockGroup,
  onUnreact,
} from '../actions.js';
import { Action, Confirm, Empty, IconButton, Pill, deleteQuestion } from './parts.js';
import { Popover } from './popover.js';
import {
  IconAddToCollection,
  IconCaret,
  IconCheck,
  IconClose,
  IconDownload,
  IconRemoveFrom,
  IconMembers,
  IconRefresh,
  IconRevoke,
  IconShareRoutes,
  IconStarFilled,
  IconTrash,
} from './icons.js';
import { kindLabel } from './space.js';
import { filmstripOf, positionOf, useThumbUrl, type Filmstrip } from './gallery.js';

/**
 * **L'inspecteur** — la nouveauté qui fait exister le modèle à l'écran.
 *
 * Le socle transporte `Description`, `Tags`, `Camera`, `DocumentDate`,
 * `Variants`, `Reactions`, `ContentRev` depuis l'étape 1 : écrits, propagés,
 * testés — et **invisibles**. Ce panneau est le premier endroit où ils
 * apparaissent.
 *
 * Deux règles y sont tenues, et elles ne se voient pas en le lisant :
 *
 * - **`UpdatedAt` n'est jamais affiché** (E7). C'est un filigrane de
 *   synchronisation qu'une réaction fait avancer ; l'appeler « modifié le »
 *   ferait dire à l'écran qu'on a édité un document qu'on a seulement aimé.
 * - **La présence d'octets est `Variants[role].size`**, jamais `State`. C'est le
 *   seul drapeau honnête, et le seul qui permette de dire « la source est là »
 *   sans mentir.
 */
export function Inspector(): JSX.Element | null {
  const fid = selection.value;
  if (!fid) return null;

  const entity = detail.value;

  return (
    <aside class="inspector" data-testid="inspector" aria-label={t('inspector.title')}>
      <div class="inspector-head">
        <h3>{t('inspector.title')}</h3>
        <IconButton
          testId="inspector-close"
          label={t('viewer.close')}
          onClick={closeInspector}
        >
          <IconClose size={18} />
        </IconButton>
      </div>

      {detailBusy.value && !entity ? (
        <p class="notice busy" role="status">
          <span class="spinner" aria-hidden="true" />
        </p>
      ) : null}

      {entity ? <Sheet entity={entity} /> : null}
      {!entity && !detailBusy.value ? <Empty>{t('inspector.unavailable')}</Empty> : null}
    </aside>
  );
}

function Sheet({ entity }: { readonly entity: Entity }): JSX.Element {
  const id = idOf(entity);
  const source = entity.Variants?.['source'];
  const owner = useOwner(entity);
  /** Le geste destructeur en attente de confirmation, s'il y en a un. */
  const [asking, setAsking] = useState<'delete' | 'remove' | undefined>(undefined);

  return (
    <div class="inspector-body">
      <NameField entity={entity} />

      <FailureNotice entity={entity} />

      <dl class="facts">
        <dt>{t('inspector.kind')}</dt>
        <dd data-testid="inspector-kind">{kindLabel(entity)}</dd>

        {/* **À qui c'est** *(2026-08-15)*. La fiche disait tout d'un objet sauf
            la chose qui décide de ce qu'on peut en faire : renommer, ranger,
            supprimer, partager sont réservés au propriétaire, et un objet reçu
            n'en portait aucune marque. « Il n'y a pas d'indication d'à qui
            appartient l'objet » — et sans elle, un bouton absent se lit comme un
            bogue plutôt que comme un droit. */}
        <dt>{t('inspector.owner')}</dt>
        <dd data-testid="inspector-owner">{owner}</dd>

        {/* **L'heure, et pas seulement le jour** *(R4, 2026-09-12)*. La ligne
            rendait `toLocaleDateString`, ce qui suffisait à voir qu'une photo
            ancienne cesse de porter la date de son envoi — mais pas à voir ce que
            R4 fait de l'heure. L'EXIF date sans fuseau : quand l'appareil n'écrit
            pas `OffsetTimeOriginal`, le worker prend l'heure pour de l'UTC, et le
            décalage ne se lit que sur l'heure affichée.

            Elle vaut pour tous les objets, pas seulement les photos : une note et
            un document ont aussi une date de document, et deux formats pour la
            même ligne selon le type auraient été une exception de plus à tenir. */}
        <dt>{t('inspector.date')}</dt>
        <dd data-testid="inspector-date">
          {formatDateTime(entity.DocumentDate ?? entity.CreatedAt)}
        </dd>

        {/* **Les deux dates, et jamais une seule** *(2026-09-12)*. Elles ne
            répondent pas à la même question — *quand cette photo a-t-elle été
            prise* et *quand est-elle arrivée ici* —, et la fiche n'en montrait
            qu'une : celle du document, avec repli sur l'autre. Les deux se
            confondaient donc sans que rien ne le dise.

            C'est aussi ce qui rend R4 lisible à l'œil. Quand elles sont
            **égales**, aucune date de prise de vue n'est connue : c'est le
            signal exact dont `exifToFill` se sert côté serveur pour décider
            qu'il a le droit d'écrire. Quand elles diffèrent, on voit d'où vient
            la date affichée — de l'EXIF, ou d'un client qui l'a posée. */}
        <dt>{t('inspector.uploaded')}</dt>
        <dd data-testid="inspector-uploaded">{formatDateTime(entity.CreatedAt)}</dd>

        {entity.Camera ? (
          <>
            <dt>{t('inspector.camera')}</dt>
            <dd data-testid="inspector-camera">{entity.Camera}</dd>
          </>
        ) : null}

        {/* **Où la photo a été prise** *(R4, 2026-09-12)*. La donnée existait
            depuis l'app et n'avait aucun écran ; depuis R4 le worker la remplit
            aussi pour ce qui part du site, et la taire serait garder une donnée
            sensible que son propriétaire ne peut pas voir.

            Les deux ou rien : le serveur ne les écrit jamais l'une sans l'autre,
            et une latitude seule ne désigne rien. */}
        {entity.GpsLat !== undefined && entity.GpsLon !== undefined ? (
          <>
            <dt>{t('inspector.location')}</dt>
            <dd data-testid="inspector-location">
              {formatCoordinates(entity.GpsLat, entity.GpsLon)}
            </dd>
          </>
        ) : null}

        {source?.size !== undefined ? (
          <>
            <dt>{t('inspector.size')}</dt>
            <dd data-testid="inspector-size">{formatSize(source.size)}</dd>
          </>
        ) : null}

        {source?.ext ? (
          <>
            <dt>{t('inspector.format')}</dt>
            <dd>{source.ext.toUpperCase()}</dd>
          </>
        ) : null}

        {entity.ContentRev !== undefined ? (
          <>
            <dt>{t('inspector.revision')}</dt>
            <dd data-testid="inspector-rev">{entity.ContentRev}</dd>
          </>
        ) : null}
      </dl>

      <DescribedFields entity={entity} />

      <PosterField entity={entity} />
      <Variants entity={entity} />
      <Reactions entity={entity} />
      <Shares entity={entity} />

      {entity.Folder !== undefined ? <MoveField entity={entity} /> : null}

      <div class="inspector-actions">
        <DownloadAction entity={entity} />
        {/* **Ranger dans une collection depuis la fiche** *(4.6 bis)*. Le geste
            n'existait que dans le menu de la ligne : quelqu'un qui regarde un
            objet ouvert devait le refermer pour l'y mettre. Une collection n'y
            entre pas elle-même — le socle lit `FIL#<fid>`, un `aid` n'y résout
            rien. */}
        {entity.Fid ? (
          <Action testId="inspector-addto" onClick={() => onPickCollection([entity.Fid!])}>
            <IconAddToCollection size={18} />
            <span>{t('collection.addTo')}</span>
          </Action>
        ) : null}
        {/* Et l'en retirer, quand c'est **là qu'on la regarde**. Retirer n'est pas
            supprimer : l'élément reste rangé dans son dossier et dans ses autres
            collections — la phrase est dans l'écran, pas seulement dans la doc. */}
        {entity.Fid && view.value.kind === 'collection' ? (
          <Action testId="inspector-remove" onClick={() => setAsking('remove')}>
            <IconRemoveFrom size={18} />
            <span>{t('collection.remove')}</span>
          </Action>
        ) : null}
        {entity.Fid ? (
          <Action testId="inspector-delete" variant="danger" onClick={() => setAsking('delete')}>
            <IconTrash size={18} />
            <span>{t('space.delete')}</span>
          </Action>
        ) : null}
      </div>

      {/* **Les deux gestes destructeurs de la fiche se confirment aussi**
          *(2026-08-16)*. Le retour d'usage ne nommait que le menu d'objet, mais
          le même acte y partait au premier clic — et ce fichier interdit ailleurs
          qu'un geste ait deux comportements selon l'écran d'où on l'atteint.

          La confirmation se pose **sous** la rangée d'actions, jamais à sa place :
          la fiche défile, et remplacer la rangée déplacerait tout ce qui suit à
          l'instant du clic. */}
      {asking ? (
        <Confirm
          testId="inspector-confirm"
          question={
            asking === 'remove'
              ? t('collection.confirmRemove')
              : deleteQuestion(entity.Name ?? '')
          }
          note={asking === 'remove' ? t('collection.removeIsNotDelete') : undefined}
          verb={asking === 'remove' ? t('collection.remove') : t('space.delete')}
          onConfirm={() => {
            setAsking(undefined);
            if (asking === 'remove') {
              void onRemoveFromAlbum((view.value as { aid: string }).aid, entity.Fid!);
            } else {
              void onDeleteEntity(entity);
            }
          }}
          onCancel={() => setAsking(undefined)}
        />
      ) : null}

      <p class="inspector-id mono muted" data-testid="inspector-id">
        {id}
      </p>
    </div>
  );
}

/**
 * **Télécharger quoi ?** *(2026-08-14)*
 *
 * Le bouton envoyait toujours la **source**, sans le dire. C'est le mauvais
 * défaut, et pour une raison qui n'est pas d'ergonomie mais de facture : la source
 * transite en **Glacier Instant Retrieval à trente jours** (`infra`, règle
 * `source-vers-glacier-ir`), là où le rôle `standard` reste en Intelligent-Tiering.
 * Récupérer une photo de l'an dernier coûtait donc un tarif de récupération, pour
 * rendre vingt mégaoctets à quelqu'un qui voulait en envoyer une par messagerie.
 *
 * Le rôle `standard` est **exactement fait pour ça** : c'est l'image d'écran que
 * le worker produit, quelques centaines de kilo-octets, celle que la visionneuse
 * affiche déjà. Il devient donc le premier choix, et la source reste offerte —
 * avec sa taille écrite à côté, qui est l'argument le plus honnête qu'on puisse
 * donner pour choisir.
 *
 * **Un seul rôle disponible ⇒ un seul bouton.** Un menu à une entrée est un clic
 * de plus pour rien, et c'est le cas de tous les documents : ils n'ont pas de
 * dérivé, leur source *est* le fichier.
 */
const DOWNLOADABLE = ['standard', 'source', 'content'] as const;

function DownloadAction({ entity }: { readonly entity: Entity }): JSX.Element | null {
  const roles = DOWNLOADABLE.filter((role) => entity.Variants?.[role]?.size !== undefined);
  if (roles.length === 0) return null;

  const label = (role: string): string => {
    const key = `inspector.role.${role}`;
    const named = t(key);
    return named === key ? role : named;
  };

  if (roles.length === 1) {
    return (
      <Action testId="inspector-download" onClick={() => void onDownload(entity, roles[0]!)}>
        <IconDownload size={18} />
        <span>{t('inspector.download')}</span>
      </Action>
    );
  }

  return (
    <Popover
      testId="inspector-download"
      label={t('inspector.download')}
      width={264}
      trigger={() => (
        <>
          <IconDownload size={18} />
          <span>{t('inspector.download')}</span>
          <IconCaret size={14} />
        </>
      )}
    >
      {(close) => (
        <>
          {roles.map((role) => (
            <button
              key={role}
              type="button"
              role="menuitem"
              data-testid={`download-${role}`}
              onClick={() => {
                close();
                void onDownload(entity, role);
              }}
            >
              <IconDownload size={18} />
              <span class="grow">
                <span>{label(role)}</span>
                <span class="popover-note">
                  {formatSize(entity.Variants?.[role]?.size ?? 0)}
                  {/* La source **dit qu'elle dort** : c'est ce qui explique
                      pourquoi elle n'est pas le défaut, et pourquoi elle peut
                      tarder à venir sur un fichier ancien. */}
                  {role === 'source' ? ` · ${t('inspector.roleArchived')}` : ''}
                </span>
              </span>
            </button>
          ))}
        </>
      )}
    </Popover>
  );
}

/** Le nom, éditable **sur place**. Renommer ne mérite pas une boîte de dialogue. */
function NameField({ entity }: { readonly entity: Entity }): JSX.Element {
  const [value, setValue] = useState(entity.Name);
  const dirty = value.trim() !== entity.Name && value.trim().length > 0;

  return (
    <div class="form">
      <input
        class="input"
        data-testid="inspector-name"
        aria-label={t('inspector.name')}
        value={value}
        onInput={(ev) => setValue((ev.currentTarget as HTMLInputElement).value)}
        onKeyDown={(ev) => {
          if ((ev as KeyboardEvent).key === 'Enter' && dirty) void onRename(entity, value.trim());
        }}
      />
      {dirty ? (
        <Action
          testId="inspector-rename"
          variant="primary"
          onClick={() => void onRename(entity, value.trim())}
        >
          <IconCheck size={18} />
          <span>{t('inspector.rename')}</span>
        </Action>
      ) : null}
    </div>
  );
}

/**
 * **Voir, corriger, régénérer** *(R6, 2026-09-13)* — la description et les
 * mots-clés, écrits par le modèle (R5) puis **appartenant à l'utilisateur** (RC6).
 *
 * Trois choses s'y tiennent, et elles ne se voient pas en le lisant :
 *
 * - **Éditable par le propriétaire seul.** Un membre lit la description — elle
 *   voyage sur l'arête (D28) — mais `PATCH` lui rendrait `403`. Il la voit donc en
 *   texte, comme avant R6.
 * - **« décrit automatiquement » se lit par champ**, sur `Described.Machine` : une
 *   description corrigée et des mots-clés laissés à la machine sont deux états, et
 *   une seule mention pour les deux dirait faux de l'un d'eux.
 * - **« Régénérer » ne demande confirmation que s'il y a quelque chose à perdre**
 *   (Q3). Poser la question à chaque appui la viderait de son sens ; ne jamais la
 *   poser ferait perdre une correction d'un clic.
 */
function DescribedFields({ entity }: { readonly entity: Entity }): JSX.Element | null {
  const mine = entity.Owner === undefined || entity.Owner === me.value?.uid;
  const media = entity.Type === 'MEDIA' && entity.Fid !== undefined;

  if (!mine) {
    return (
      <>
        {entity.Description ? (
          <p class="inspector-description" data-testid="inspector-description">
            {entity.Description}
          </p>
        ) : null}
        {entity.Tags && entity.Tags.length > 0 ? (
          <div class="tags" data-testid="inspector-tags">
            {entity.Tags.map((tag) => (
              <Pill key={tag}>{tag}</Pill>
            ))}
          </div>
        ) : null}
      </>
    );
  }

  return (
    // La clé remonte les champs quand la description change sous eux — après
    // « Régénérer », la synchronisation apporte un texte neuf, et un brouillon
    // local périmé ne doit pas le masquer.
    <section class="described" data-testid="inspector-described" key={`${idOf(entity)}|${entity.Described?.At ?? ''}`}>
      <DescriptionEditor entity={entity} />
      <TagsEditor entity={entity} />
      {media ? <Regenerate entity={entity} /> : null}
    </section>
  );
}

function MachineMark({ entity, field }: { readonly entity: Entity; readonly field: 'Description' | 'Tags' }) {
  return heldByMachine(entity, field) ? (
    <span class="machine-mark" data-testid={`machine-${field}`}>
      {t('inspector.machine')}
    </span>
  ) : null;
}

function DescriptionEditor({ entity }: { readonly entity: Entity }): JSX.Element {
  const saved = entity.Description ?? '';
  const [value, setValue] = useState(saved);
  const dirty = value.trim() !== saved.trim();

  return (
    <div class="described-field">
      <label for="inspector-description" class="described-label">
        <span>{t('inspector.description')}</span>
        <MachineMark entity={entity} field="Description" />
      </label>
      <textarea
        id="inspector-description"
        class="input"
        data-testid="inspector-description-input"
        rows={3}
        placeholder={t('inspector.descriptionPlaceholder')}
        value={value}
        onInput={(ev) => setValue((ev.currentTarget as HTMLTextAreaElement).value)}
      />
      {dirty ? (
        <Action
          testId="inspector-description-save"
          variant="primary"
          // Vider le champ **efface** : `null` est la valeur qu'écrit un effacement,
          // et elle reprend le champ à la machine au même titre qu'une réécriture.
          onClick={() => void onSaveDescribed(entity, { description: value.trim() || null })}
        >
          <IconCheck size={18} />
          <span>{t('inspector.save')}</span>
        </Action>
      ) : null}
    </div>
  );
}

/**
 * Les mots-clés : **chaque geste enregistre**, retirer comme ajouter. Un ensemble
 * n'a pas de brouillon utile — et une liste qu'on corrige mot par mot sans que rien
 * ne parte serait perdue au premier changement de fiche.
 *
 * Le pré-contrôle lit les bornes **servies** par `/me` (`fields`), jamais recopiées :
 * le serveur reste juge, l'écran évite seulement un refus prévisible.
 */
function TagsEditor({ entity }: { readonly entity: Entity }): JSX.Element {
  const tags = entity.Tags ?? [];
  const [draft, setDraft] = useState('');
  const maxTags = me.value?.fields?.['maxTags'] ?? 32;

  const add = (): void => {
    const tag = draft.trim().replace(/\s+/g, ' ').toLocaleLowerCase('fr');
    if (!tag || tags.includes(tag)) {
      setDraft('');
      return;
    }
    setDraft('');
    void onSaveDescribed(entity, { tags: [...tags, tag] });
  };

  return (
    <div class="described-field">
      <span class="described-label">
        <span>{t('inspector.tags')}</span>
        <MachineMark entity={entity} field="Tags" />
      </span>
      {tags.length > 0 ? (
        <div class="tags" data-testid="inspector-tags">
          {tags.map((tag) => (
            <span key={tag} class="pill tag-editable">
              {tag}
              <button
                type="button"
                class="tag-remove"
                data-testid={`tag-remove-${tag}`}
                aria-label={t('inspector.tagRemove', { tag })}
                onClick={() => void onSaveDescribed(entity, { tags: tags.filter((x) => x !== tag) })}
              >
                <IconClose size={12} />
              </button>
            </span>
          ))}
        </div>
      ) : null}
      {tags.length < maxTags ? (
        <input
          class="input"
          data-testid="inspector-tag-add"
          aria-label={t('inspector.tagAdd')}
          placeholder={t('inspector.tagAdd')}
          value={draft}
          onInput={(ev) => setDraft((ev.currentTarget as HTMLInputElement).value)}
          onKeyDown={(ev) => {
            const key = (ev as KeyboardEvent).key;
            if (key === 'Enter' || key === ',') {
              ev.preventDefault();
              add();
            }
          }}
        />
      ) : null}
    </div>
  );
}

function Regenerate({ entity }: { readonly entity: Entity }): JSX.Element | null {
  const [asking, setAsking] = useState(false);
  if (!me.value?.features?.describe || entity.State !== 'READY') return null;

  const pending = entity.Fid !== undefined && describing.value.has(entity.Fid);
  if (pending) {
    return (
      <p class="hint" data-testid="describe-pending" role="status">
        <span class="spinner" aria-hidden="true" /> {t('describe.queued')}
      </p>
    );
  }

  return (
    <>
      {entity.Described === undefined && !entity.Description && !(entity.Tags?.length) ? (
        <p class="hint" data-testid="describe-none">
          {t('describe.pending')}
        </p>
      ) : null}
      <Action
        testId="describe-regenerate"
        variant="quiet"
        onClick={() => (hasCorrections(entity) ? setAsking(true) : void onRegenerate(entity))}
      >
        <IconRefresh size={16} />
        <span>{t('describe.regenerate')}</span>
      </Action>
      {asking ? (
        <Confirm
          testId="describe-confirm"
          question={t('describe.confirm')}
          note={t('describe.confirmNote')}
          verb={t('describe.confirmVerb')}
          onConfirm={() => {
            setAsking(false);
            void onRegenerate(entity);
          }}
          onCancel={() => setAsking(false)}
        />
      ) : null}
    </>
  );
}

/**
 * Le déplacement.
 *
 * Une liste des dossiers **connus**, et pas un champ libre : `set_folder` refuse
 * un dossier inexistant (`409 folder_unknown`), et laisser taper un chemin
 * fabriquerait une erreur évitable.
 *
 * **Tous les fichiers l'ont** depuis 4.5, photos comprises. Le contrôle reste
 * conditionné à la présence de `Folder` et non à une liste de types : une
 * collection n'en porte pas, et c'est cette absence — non un test sur `Aid` —
 * qui l'écarte.
 */
function MoveField({ entity }: { readonly entity: Entity }): JSX.Element {
  return (
    <div class="setting">
      <label for="inspector-folder">{t('inspector.folder')}</label>
      <select
        id="inspector-folder"
        class="input"
        data-testid="inspector-folder"
        value={entity.Folder ?? ROOT}
        onChange={(ev) => {
          const target = (ev.currentTarget as HTMLSelectElement).value;
          if (target !== entity.Folder) void onMove(entity, target);
        }}
      >
        {[...folders.value]
          .sort((a, b) => a.folder.localeCompare(b.folder))
          .map((f) => (
            <option key={f.folder} value={f.folder}>
              {f.folder === ROOT ? t('space.root') : f.folder}
            </option>
          ))}
      </select>
    </div>
  );
}

/**
 * **L'affiche d'une vidéo** (M6) — et le sélecteur est la pellicule elle-même.
 *
 * Les huit cases sont **déjà là**, dans la seule image que la grille a téléchargée
 * pour peindre la vignette. Les proposer au choix ne coûte donc ni une requête, ni
 * un octet, ni un réveil de la source qui dort en Glacier IR : ce sont huit
 * fenêtres sur la même planche, et le clic écrit un entier.
 *
 * Deux choses s'y tiennent, et ne se voient pas en le lisant :
 *
 * - **Réservé au propriétaire.** `PATCH /files/{fid}` rendrait `403` à un lecteur,
 *   et un contrôle qui ne peut qu'échouer est pire qu'un contrôle absent. La règle
 *   est celle de tout ce fichier : `Owner` n'est rendu **que** sur un objet reçu,
 *   donc son absence affirme « c'est à moi ».
 * - **Rien à choisir sans planche.** `filmstripOf` ne rend une géométrie que
 *   lorsque `Variants.thumb.frames` existe — jamais pour une photo, et pas encore
 *   pour une vidéo dont le worker n'a pas fini. Le serveur refuserait d'ailleurs
 *   l'écriture (`400 poster_frame_unsupported`), faute de borne à opposer.
 */
function PosterField({ entity }: { readonly entity: Entity }): JSX.Element | null {
  const fid = entity.Fid;
  const strip = filmstripOf(entity);
  const mine = entity.Owner === undefined || entity.Owner === me.value?.uid;
  // Les gardes vivent **ici**, au-dessus du crochet : `useThumbUrl` ne doit être
  // appelé que sur un `fid` qui existe, et un crochet ne se conditionne pas.
  if (!fid || !strip || !mine) return null;
  return <PosterPicker entity={entity} fid={fid} strip={strip} />;
}

function PosterPicker({
  entity,
  fid,
  strip,
}: {
  readonly entity: Entity;
  readonly fid: string;
  readonly strip: Filmstrip;
}): JSX.Element | null {
  // L'URL est déjà connue neuf fois sur dix : la grille l'a demandée **en lot**
  // pour peindre la vignette, et le cache la rend sans un appel.
  const { src } = useThumbUrl(fid);
  if (!src) return null;

  return (
    <section class="poster" data-testid="inspector-poster">
      <h4>{t('poster.title')}</h4>
      <div class="poster-frames" role="group" aria-label={t('poster.title')}>
        {Array.from({ length: strip.frames }, (_, i) => (
          <button
            key={i}
            type="button"
            class={i === strip.frame ? 'poster-frame on' : 'poster-frame'}
            data-testid={`poster-frame-${i}`}
            aria-pressed={i === strip.frame}
            aria-label={t('poster.frame', { n: i + 1 })}
            style={{
              backgroundImage: `url("${src}")`,
              backgroundSize: `${strip.frames * 100}% 100%`,
              // La même arithmétique que la vignette, par la **même** fonction :
              // deux façons de fenêtrer la même planche finiraient par montrer
              // deux cases différentes pour un seul entier.
              backgroundPositionX: `${positionOf({ frames: strip.frames, frame: i })}%`,
            }}
            // Recliquer la case active la **retire** : `null` ramène au défaut, et
            // sans ce retour l'affiche ne pourrait plus qu'être remplacée. C'est le
            // geste des réactions, appliqué à un entier.
            onClick={() => void onPickPoster(entity, i === strip.frame ? null : i)}
          />
        ))}
      </div>
      <p class="action-note">{t('poster.help')}</p>
    </section>
  );
}

/**
 * Les variantes, **avec leur présence réelle**.
 *
 * C'est la traduction à l'écran de la règle : une variante annoncée sans
 * `size` n'a pas d'octets constatés, donc pas d'URL, donc rien à télécharger.
 * L'afficher comme absente plutôt que de la taire évite la question « pourquoi
 * la vignette ne s'affiche pas ».
 */
/**
 * **L'échec, nommé, et le geste qui le défait** *(2026-09-12)*.
 *
 * Il n'existait rien de tout ça. Une photo en échec portait « échec du traitement »
 * sur sa tuile, une pastille `standard · à venir` dans sa fiche, et aucune sortie :
 * 42 photos y sont restées en production sans qu'un écran dise pourquoi ni qu'on
 * puisse rien tenter. Le motif était pourtant dans la réponse depuis toujours.
 *
 * Il est **avant les faits** et non sous les variantes : sur un objet en échec,
 * c'est la seule chose qu'on vient lire. Et il est réservé au **propriétaire**,
 * comme la route qu'il appelle — proposer à un membre un bouton qui répondrait
 * `403` est pire que de ne rien proposer, c'est déjà la règle de la barre de
 * collection.
 *
 * `PROCESSING` n'y paraît pas : ce n'est pas un échec, et un encart d'alerte sur un
 * traitement en cours ferait craindre une panne là où il n'y a qu'une attente.
 */
function FailureNotice({ entity }: { readonly entity: Entity }): JSX.Element | null {
  if (entity.State !== 'FAILED') return null;

  const fid = entity.Fid;
  const mine = me.value !== undefined && entity.Owner === me.value.uid;
  const why = failReasonLabel(entity.FailReason);

  return (
    <section class="notice warning" data-testid="inspector-failure" role="status">
      <p data-testid="failure-reason">{why ?? t('entity.failed')}</p>
      {mine && fid ? (
        <>
          <Action testId="retry-derivatives" onClick={() => void onRetryDerivatives(fid)}>
            {t('entity.retry')}
          </Action>
          {/* **Dire que les octets ne repartent pas** : sur une photo de 38 Mio
              derrière une liaison lente, c'est la différence entre appuyer et ne
              pas oser. */}
          <p class="hint" data-testid="retry-hint">
            {t('entity.retryHint')}
          </p>
        </>
      ) : null}
    </section>
  );
}

function Variants({ entity }: { readonly entity: Entity }): JSX.Element | null {
  const variants = Object.entries(entity.Variants ?? {});
  if (variants.length === 0) return null;

  return (
    <div class="variants" data-testid="inspector-variants">
      {variants.map(([role, variant]) => (
        <Pill key={role} tone={variant.size === undefined ? 'warning' : 'neutral'}>
          {role}
          {variant.size === undefined ? ` · ${t('inspector.pending')}` : ` · ${formatSize(variant.size)}`}
        </Pill>
      ))}
    </div>
  );
}

/**
 * Les réactions : emoji et notes 1→5 (D24), **et on peut enfin en poser une**.
 *
 * Trois choses s'y tiennent :
 *
 * - **La moyenne est calculée ici**, jamais stockée — un agrégat serveur serait un
 *   compteur de plus à réparer, et il finirait par diverger d'un dixième pour
 *   l'éternité.
 * - **Réagir est ouvert à quiconque peut lire**, propriétaire ou non. C'était
 *   structurellement impossible en V1, et c'est le seul retour entre membres d'un
 *   groupe depuis que les posts ont disparu (C3, D12).
 * - **Ce qui se synchronise est un agrégat**, jamais la liste des réacteurs : sur
 *   une entité reçue, sa propre entrée se relit par `GET /files/{fid}` — ce que
 *   fait l'inspecteur —, elle n'arrive pas par la page de synchronisation.
 *
 * La **modération** est arrivée en 4.6, et elle est repliée sous un bouton : elle
 * demande de nommer les réacteurs, ce qui est une lecture de plus et une donnée
 * qu'on ne montre pas sans qu'on la demande.
 */
function Reactions({ entity }: { readonly entity: Entity }): JSX.Element | null {
  const fid = entity.Fid;
  if (!fid) return null;

  // **`reactions.mine` est servi par le serveur** : sa propre entrée, et rien que
  // la sienne. Aller la chercher dans une map nominative par `uid` ne marcherait
  // pas — cette map ne sort d'aucune route, et un client qui la lisait ne trouvait
  // jamais rien. C'est le défaut qu'a révélé le premier parcours de 4.4.
  const agg = entity.reactions;
  const mine = agg?.mine ?? undefined;
  const average = agg?.scoreAvg ?? undefined;
  const counts = Object.entries(agg?.emoji ?? {});

  return (
    <section class="reactions" data-testid="inspector-reactions">
      <h4>{t('reaction.title')}</h4>

      <div class="stars" role="group" aria-label={t('reaction.score')}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            class={mine?.score !== undefined && mine.score >= n ? 'star on' : 'star'}
            data-testid={`react-score-${n}`}
            aria-pressed={mine?.score === n}
            aria-label={t('reaction.stars', { count: n })}
            // Recliquer sa propre note la retire : sans cela, une note posée par
            // erreur ne pourrait plus qu'être remplacée, jamais annulée.
            onClick={() => void onReact(fid, { score: mine?.score === n ? undefined : n })}
          >
            <IconStarFilled size={20} />
          </button>
        ))}
      </div>

      <div class="emoji-row" role="group" aria-label={t('reaction.emoji')}>
        {emojiPalette.value.map((symbol) => (
          <button
            key={symbol}
            type="button"
            class={mine?.emoji === symbol ? 'emoji-chip on' : 'emoji-chip'}
            data-testid={`react-emoji-${symbol}`}
            aria-pressed={mine?.emoji === symbol}
            // Recliquer l'emoji actif le retire **en gardant la note** : c'est la
            // fusion d'`onReact` qui le permet, et sans elle l'un effacerait l'autre.
            onClick={() =>
              void onReact(fid, { emoji: mine?.emoji === symbol ? undefined : symbol })
            }
          >
            {symbol}
          </button>
        ))}
      </div>

      {mine ? (
        <Action testId="react-clear" variant="quiet" onClick={() => void onUnreact(fid)}>
          {t('reaction.clear')}
        </Action>
      ) : null}

      {average !== undefined || counts.length > 0 ? (
        <div class="tags" data-testid="inspector-reactions-summary">
          {average !== undefined ? (
            <Pill tone="share">
              <IconStarFilled size={13} />
              {average.toFixed(1)} · {t('inspector.votes', { count: agg?.scoreCount ?? 0 })}
            </Pill>
          ) : null}
          {counts.map(([symbol, count]) => (
            <Pill key={symbol}>
              {symbol} {count}
            </Pill>
          ))}
        </div>
      ) : null}

      <Moderation entity={entity} fid={fid} />
    </section>
  );
}

/**
 * **Le propriétaire, nommé quand on peut le nommer** *(2026-08-15)*.
 *
 * `Owner` est un ULID opaque — c'est voulu, un `uid` ne dit rien de personne — et
 * il n'est rendu **qu'aux lecteurs d'un objet reçu** : sur les siens, l'absence du
 * champ est le cas courant. Le test porte donc sur « ce n'est pas à quelqu'un
 * d'autre », comme partout ailleurs dans ce fichier.
 *
 * Le nom se cherche dans les membres **déjà chargés**, et se demande sinon pour
 * les groupes par lesquels l'objet arrive — `authGroups` est filtré au point de
 * vue de l'appelant, donc ce sont exactement les cercles qu'on partage avec le
 * propriétaire. Un appel, une fois, et seulement quand la fiche est ouverte sur un
 * objet qui n'est pas à soi : c'est une donnée nominative, elle ne se précharge
 * pas « au cas où ».
 *
 * Faute de nom, on écrit **« quelqu'un d'autre »** et jamais le ULID : afficher un
 * identifiant à la place d'un nom donne l'illusion d'une information.
 */
function useOwner(entity: Entity): string {
  const uid = entity.Owner;
  const mine = uid === undefined || uid === me.value?.uid;
  const known = mine ? undefined : findMember(uid);
  const groupsOf = entity.authGroups;

  useEffect(() => {
    if (mine || known !== undefined) return;
    for (const gid of groupsOf ?? []) void loadMembers(gid).catch(() => undefined);
  }, [uid, known, groupsOf]);

  if (mine) return t('inspector.ownerMe');
  return known ?? t('inspector.ownerOther');
}

function findMember(uid: string | undefined): string | undefined {
  if (uid === undefined) return undefined;
  for (const list of Object.values(members.value)) {
    const found = list.find((m) => m.uid === uid);
    if (found) return found.alias ?? t('group.unnamed');
  }
  return undefined;
}

/**
 * **La modération** (4.6) — retirer de son propre objet la réaction d'un autre.
 *
 * Elle n'existait pas à l'écran, et pas seulement à l'écran : la route
 * `DELETE /files/{fid}/reactions/{uid}` était écrite depuis 4.4 et **aucun client
 * ne pouvait former son appel**, le `uid` d'un réacteur ne sortant d'aucune
 * réponse. Une capacité spécifiée, testée, et structurellement inatteignable.
 *
 * Elle n'est proposée **qu'au propriétaire** — la route rendrait `403` — et les
 * noms ne sont chargés qu'au clic : c'est une donnée nominative, elle ne s'affiche
 * pas sans qu'on la demande.
 */
function Moderation({
  entity,
  fid,
}: {
  readonly entity: Entity;
  readonly fid: string;
}): JSX.Element | null {
  // `Owner` n'est rendu qu'aux lecteurs d'un objet reçu ; sur les siens, l'absence
  // du champ est le cas courant. Le test porte donc sur « ce n'est pas à
  // quelqu'un d'autre », jamais sur une égalité stricte qui masquerait le bouton
  // sur ses propres objets.
  const mine = entity.Owner === undefined || entity.Owner === me.value?.uid;
  const total = (entity.reactions?.scoreCount ?? 0) + Object.keys(entity.reactions?.emoji ?? {}).length;
  if (!mine || total === 0) return null;

  const loaded = reactors.value?.fid === fid ? reactors.value.items : undefined;

  if (loaded === undefined) {
    return (
      <Action testId="who-reacted" variant="quiet" onClick={() => void onLoadReactors(fid)}>
        <IconMembers size={16} />
        <span>{t('reaction.who')}</span>
      </Action>
    );
  }

  if (loaded.length === 0) return <Empty>{t('reaction.nobody')}</Empty>;

  return (
    <ul class="reactors" data-testid="reactors">
      {loaded.map((r) => (
        <li key={r.uid} class="reactor" data-testid={`reactor-${r.uid}`}>
          <span class="grow">{r.alias ?? t('group.unnamed')}</span>
          <span class="reactor-entry">
            {r.emoji ?? ''}
            {r.score !== null ? (
              <>
                <IconStarFilled size={12} />
                {r.score}
              </>
            ) : null}
          </span>
          <IconButton
            testId={`moderate-${r.uid}`}
            label={t('reaction.moderate', { name: r.alias ?? r.uid })}
            onClick={() => void onModerateReaction(fid, r.uid)}
          >
            <IconRevoke size={16} />
          </IconButton>
        </li>
      ))}
    </ul>
  );
}

/**
 * **« Où va ma donnée »** (D15) — la seule réponse honnête à *qui voit ceci ?*
 *
 * Elle vit ici et non dans une liste globale parce que la question se pose
 * **toujours à propos d'une chose** : « à qui ai-je donné ceci ? » a un objet
 * direct, et un inventaire « ce que j'ai donné » aurait redérivé la même donnée
 * sans offrir où agir.
 *
 * Trois choses s'y tiennent :
 *
 * - **Les raisons sont affichées**, pas seulement les groupes. Un accès peut
 *   venir de plusieurs collections à la fois ; ne montrer que le groupe ferait
 *   croire qu'en retirer l'objet d'une collection suffit à couper l'accès.
 * - **Le retrait est souverain** : il coupe *toutes* les raisons d'un coup et
 *   inscrit le groupe en exclusion. Sans cette inscription il serait cosmétique —
 *   la propagation suivante de n'importe quelle collection partagée à ce groupe le
 *   rétablirait, et le réconciliateur aussi.
 * - **Lever le blocage ne repartage pas.** Rendre le droit demande un acte de
 *   partage neuf ; confondre les deux ferait ressusciter un accès qu'on avait
 *   coupé, à un moment qu'on n'aurait pas choisi.
 *
 * Réservée au propriétaire, et il n'y a pas d'alternative défendable : la liste des
 * groupes destinataires *est* le graphe social de celui qui partage.
 */
function Shares({ entity }: { readonly entity: Entity }): JSX.Element | null {
  const fid = entity.Fid;
  const mine = entity.Owner === undefined || entity.Owner === me.value?.uid;
  if (!fid || !mine) return null;

  const loaded = shares.value?.fid === fid ? shares.value : undefined;
  const named = (gid: string): string =>
    (groups.value ?? []).find((g) => g.gid === gid)?.name ??
    (me.value?.groups ?? []).find((g) => g.gid === gid)?.name ??
    gid;

  if (!loaded) {
    return (
      <Action testId="load-shares" variant="quiet" onClick={() => void onLoadShares(fid)}>
        <IconShareRoutes size={16} />
        <span>{t('share.where')}</span>
      </Action>
    );
  }

  return (
    <section class="shares" data-testid="inspector-shares">
      <h4>{t('share.where')}</h4>

      {loaded.shares.length === 0 ? (
        <Empty>{t('share.nowhere')}</Empty>
      ) : (
        <ul class="share-list">
          {loaded.shares.map((s) => (
            <li key={s.gid} class="share" data-testid={`share-${s.gid}`}>
              <span class="grow">
                <span class="share-group">{named(s.gid)}</span>
                {/* Les **raisons** : `direct`, ou une collection par entrée. */}
                <span class="share-via">
                  {s.via
                    .map((via) =>
                      via.startsWith('ALB#')
                        ? ((albums.value ?? []).find((a) => a.Aid === via.slice(4))?.Name ??
                          t('share.viaCollection'))
                        : t('share.viaDirect'),
                    )
                    .join(' · ')}
                </span>
              </span>
              <IconButton
                testId={`revoke-${s.gid}`}
                label={t('share.revoke', { name: named(s.gid) })}
                onClick={() => void onRevokeGroup(fid, s.gid)}
              >
                <IconRevoke size={16} />
              </IconButton>
            </li>
          ))}
        </ul>
      )}

      {loaded.excluded.length > 0 ? (
        <ul class="share-list excluded" data-testid="inspector-excluded">
          {loaded.excluded.map((gid) => (
            <li key={gid} class="share" data-testid={`excluded-${gid}`}>
              <span class="grow">
                <span class="share-group">{named(gid)}</span>
                <span class="share-via">{t('share.blocked')}</span>
              </span>
              <Action testId={`unblock-${gid}`} variant="quiet" onClick={() => void onUnblockGroup(fid, gid)}>
                {t('share.unblock')}
              </Action>
            </li>
          ))}
        </ul>
      ) : null}

      {loaded.history.length > 0 ? (
        <ol class="history" data-testid="inspector-share-history">
          {loaded.history.map((h, i) => (
            <li key={`${h.at}-${i}`}>
              <span class="mono">{formatDate(h.at)}</span> {t(`share.action.${h.action}`)}
            </li>
          ))}
        </ol>
      ) : null}
    </section>
  );
}
